import { Component, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { lastValueFrom } from 'rxjs';
import { AppConstants } from 'src/app/shared/constants/app.contants';
import { ApiService } from 'src/app/shared/services/api.service';
import { LoaderService } from 'src/app/shared/services/loader.service';
import { ToastService } from 'src/app/shared/services/toast.service';
import { environment } from 'src/environments/environment';

interface FAQ {
  id?: number;
  faqId?: string;
  question: string;
  answer: string;
  linkedTenderId?: string;
  linkedTenderTitle?: string;
  status?: string;
  createdDate?: Date;
}

@Component({
  selector: 'app-faqs',
  templateUrl: './faqs.component.html',
  styleUrls: ['./faqs.component.scss']
})
export class FAQsComponent implements OnInit {
  faqs: FAQ[] = [];
  AppConstants = AppConstants;

  constructor(
    private _http: HttpClient,
    private _loaderService: LoaderService,
    private _toastService: ToastService
  ) { }

  ngOnInit(): void {
    this.loadMockFAQs();
  }

  /**
   * Load mock FAQs data
   * TODO: Replace with actual API call when ready
   */
  private loadMockFAQs(): void {
    // Simulate loading delay
    setTimeout(() => {
      this.faqs = this.getMockFAQs();
    }, 300);
  }

  /**
   * Get mock FAQs data
   */
  private getMockFAQs(): FAQ[] {
    return [
      {
        id: 1,
        faqId: 'FAQ-001',
        question: 'How do I register as a vendor?',
        answer: 'To register as a vendor, you need to complete the vendor application form. Navigate to the Vendor Application section and fill in all required information including company details, contact information, and required documents. Once submitted, your application will be reviewed by our team.',
        linkedTenderId: '',
        linkedTenderTitle: '',
        status: 'Published',
        createdDate: new Date('2024-01-15')
      },
      {
        id: 2,
        faqId: 'FAQ-002',
        question: 'What documents are required for vendor registration?',
        answer: 'The required documents include:\n\n1. Commercial Registration Certificate\n2. Valid Trade License\n3. Tax Identification Number\n4. Bank Account Details\n5. Company Profile\n6. Previous Experience Certificates\n\nAll documents must be valid and up-to-date.',
        linkedTenderId: '',
        linkedTenderTitle: '',
        status: 'Published',
        createdDate: new Date('2024-01-20')
      },
      {
        id: 3,
        faqId: 'FAQ-003',
        question: 'How long does the vendor approval process take?',
        answer: 'The vendor approval process typically takes 5-10 business days after submission of a complete application. The timeline may vary depending on the completeness of your documentation and the volume of applications being processed.',
        linkedTenderId: '',
        linkedTenderTitle: '',
        status: 'Published',
        createdDate: new Date('2024-02-01')
      },
      {
        id: 4,
        faqId: 'FAQ-004',
        question: 'How do I submit a bid for a tender?',
        answer: 'To submit a bid:\n\n1. Browse available tenders in the Tender List section\n2. Click on a tender to view details\n3. Review all requirements and documents\n4. Prepare your bid proposal\n5. Upload required documents\n6. Submit your bid before the closing date\n\nMake sure to review all tender requirements carefully before submission.',
        linkedTenderId: 'TND-2024-001',
        linkedTenderTitle: 'IT Infrastructure Upgrade Project',
        status: 'Published',
        createdDate: new Date('2024-02-10')
      },
      {
        id: 5,
        faqId: 'FAQ-005',
        question: 'Can I modify my bid after submission?',
        answer: 'Bids can only be modified before the tender closing date and time. Once the tender has closed, no modifications are allowed. If you need to make changes, you can withdraw your current bid and submit a new one before the deadline.',
        linkedTenderId: '',
        linkedTenderTitle: '',
        status: 'Published',
        createdDate: new Date('2024-02-15')
      },
      {
        id: 6,
        faqId: 'FAQ-006',
        question: 'How will I be notified about tender awards?',
        answer: 'You will receive notifications through:\n\n1. Email notifications to your registered email address\n2. In-app notifications in the Notifications section\n3. Status updates in your Bid List\n\nMake sure your contact information is up-to-date to receive all notifications promptly.',
        linkedTenderId: '',
        linkedTenderTitle: '',
        status: 'Published',
        createdDate: new Date('2024-02-20')
      },
      {
        id: 7,
        faqId: 'FAQ-007',
        question: 'What payment methods are accepted?',
        answer: 'We accept various payment methods including bank transfers, credit cards, and other approved payment gateways. Payment terms and methods may vary depending on the specific contract or purchase order. Check your financial dashboard for payment details and history.',
        linkedTenderId: '',
        linkedTenderTitle: '',
        status: 'Published',
        createdDate: new Date('2024-03-01')
      },
      {
        id: 8,
        faqId: 'FAQ-008',
        question: 'How do I update my vendor profile information?',
        answer: 'To update your vendor profile:\n\n1. Navigate to the Profile section\n2. Click on Edit Profile\n3. Update the information you need to change\n4. Upload any new documents if required\n5. Save your changes\n\nNote: Some changes may require approval before they take effect.',
        linkedTenderId: '',
        linkedTenderTitle: '',
        status: 'Published',
        createdDate: new Date('2024-03-05')
      }
    ];
  }

  /**
   * Create API service instance
   * TODO: Use this method when ready to call actual API
   */
  private createApiService(): ApiService {
    const apiService = new ApiService(this._http);
    apiService['_baseUrl'] = environment.API_URL;
    return apiService;
  }

  /**
   * Fetch FAQs from API
   * TODO: Uncomment and use this method when ready to call actual API
   */
  // async fetchFAQs(): Promise<void> {
  //   this._loaderService.showLoader();
  //   
  //   try {
  //     const apiService = this.createApiService();
  //     
  //     const response: any = await lastValueFrom(
  //       apiService.get(
  //         AppConstants.CONTROLLERS.FAQs,
  //         AppConstants.ACTIONS.GetAllFAQs,
  //         true,
  //         environment.APIs_PREFIX
  //       )
  //     );

  //     // Handle different response formats
  //     let faqsData: any[] | null = null;
  //     
  //     if (response && (response.IsSuccess || response.isSuccess)) {
  //       // Check for nested data.data structure
  //       if (response.data && response.data.data && Array.isArray(response.data.data)) {
  //         faqsData = response.data.data;
  //       } else if (response.data && Array.isArray(response.data)) {
  //         faqsData = response.data;
  //       } else if (Array.isArray(response)) {
  //         faqsData = response;
  //       }
  //       
  //       if (faqsData && Array.isArray(faqsData)) {
  //         this.faqs = this.mapFAQsResponse(faqsData);
  //       } else {
  //         this.faqs = [];
  //       }
  //     } else {
  //       const errorMessage = response?.ErrorMessageEn || response?.errorMessageEn || 'Failed to load FAQs';
  //       this._toastService.error(errorMessage, 'Error');
  //       this.faqs = [];
  //     }
  //   } catch (error: any) {
  //     console.error('Error fetching FAQs:', error);
  //     const errorMessage = error?.error?.ErrorMessageEn || 
  //                         error?.error?.errorMessageEn || 
  //                         error?.message || 
  //                         'An error occurred while loading FAQs';
  //     this._toastService.error(errorMessage, 'Error');
  //     this.faqs = [];
  //   } finally {
  //     this._loaderService.hideLoader();
  //   }
  // }

  /**
   * Map API response to FAQ model
   * TODO: Use this method when ready to call actual API
   */
  // private mapFAQsResponse(data: any[]): FAQ[] {
  //   return data.map((item: any) => ({
  //     id: item.id || item.ID || item.Id,
  //     faqId: item.faqId || item.FAQId || '',
  //     question: item.question || item.Question || '',
  //     answer: item.answer || item.Answer || '',
  //     linkedTenderId: item.linkedTenderId || item.LinkedTenderId || '',
  //     linkedTenderTitle: item.linkedTenderTitle || item.LinkedTenderTitle || item.tender?.tenderName || item.tender?.TenderName || '',
  //     status: item.status || item.Status || '',
  //     createdDate: item.createdDate || item.CreatedDate ? new Date(item.createdDate || item.CreatedDate) : undefined
  //   }));
  // }
}

